import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Stethoscope, ArrowRight, Loader2, Sparkles } from 'lucide-react';
import CarePageHeader from '@/components/CarePageHeader';
import { Button } from '@/components/ui/button';
import { API_BASE } from '@/config/api';
import { getAuthToken } from '@/utils/auth';
import '@/components/care-page.css';

const FALLBACK_CANCERS = [
  'Breast Cancer',
  'Lung Cancer',
  'Colorectal Cancer',
  'Prostate Cancer',
  'Pancreatic Cancer',
  'Ovarian Cancer',
  'Leukemia',
  'Lymphoma',
  'Brain Tumor',
  'Head and Neck Cancer',
  'Melanoma',
];

const TREATMENT_OPTIONS = [
  '',
  'Surgery',
  'Chemotherapy',
  'Radiation',
  'Immunotherapy',
  'Targeted therapy',
  'Transplant',
  'Proton therapy',
  'Clinical trial',
];

const LANGUAGE_OPTIONS = ['', 'English', 'Hindi', 'Spanish', 'Arabic', 'Mandarin', 'French'];

export default function GetMatched() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resultsRef = useRef(null);
  const [cancerType, setCancerType] = useState(searchParams.get('cancer_type') || '');
  const [city, setCity] = useState(searchParams.get('city') || '');
  const [international, setInternational] = useState(searchParams.get('international') === '1');
  const [treatmentType, setTreatmentType] = useState(searchParams.get('treatment') || '');
  const [preferredLanguage, setPreferredLanguage] = useState(searchParams.get('language') || '');
  const [insurance, setInsurance] = useState(searchParams.get('insurance') || '');
  const [memberId, setMemberId] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [cancerOptions, setCancerOptions] = useState(FALLBACK_CANCERS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [hasRun, setHasRun] = useState(false);

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API_BASE,
      withCredentials: true,
    });
    instance.interceptors.request.use((config) => {
      const token = getAuthToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
    return instance;
  }, []);

  useEffect(() => {
    let alive = true;
    api
      .get('/cancer-types')
      .then((res) => {
        const rows =
          res.data?.all_cancers ||
          res.data?.common_cancers ||
          res.data?.cancer_types ||
          res.data ||
          [];
        const names = (Array.isArray(rows) ? rows : [])
          .map((row) => (typeof row === 'string' ? row : row?.name || row?.label || row?.type))
          .filter(Boolean);
        if (alive && names.length) setCancerOptions([...new Set(names)]);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [api]);

  const runMatch = async (event) => {
    event?.preventDefault?.();
    setError('');
    setResult(null);
    if (!cancerType.trim()) {
      setError('Choose a cancer type so we can filter verified centre profiles.');
      return;
    }
    setLoading(true);
    setHasRun(true);
    try {
      const res = await api.post('/match-hospitals', {
        cancer_type: cancerType.trim(),
        city: city.trim() || null,
        international_patient: international,
        treatment_type: treatmentType || null,
        preferred_language: preferredLanguage || null,
        insurance: insurance.trim() || null,
        member_id: memberId.trim() || null,
        date_of_birth: dateOfBirth.trim() || null,
        include_live_quotes: true,
      });
      setResult(res.data);
      window.requestAnimationFrame(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 402) {
        setError('Hospital matching needs an active ByOnco subscription. Sign in or subscribe, then try again.');
      } else if (status === 503) {
        setError('Hospital matching is temporarily unavailable. Browse the directory or try again shortly.');
      } else {
        setError(
          err?.response?.data?.detail ||
            err?.message ||
            'Matching failed. Try again in a moment.',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const type = searchParams.get('cancer_type') || cancerType;
    if (searchParams.get('autostart') === '1' && type) {
      if (!cancerType && type) setCancerType(type);
      runMatch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hospitals = result?.hospitals || [];
  const doctors = result?.doctors || [];
  const recommendation = result?.ai_recommendation || '';
  const shortlist = result?.shortlist || [];
  const packageQuery = new URLSearchParams({
    cancer_type: cancerType.trim(),
    ...(city.trim() ? { city: city.trim() } : {}),
  }).toString();

  return (
    <div className="care-page min-h-screen bg-cream-paper">
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-10 pb-16">
        <CarePageHeader
          current="/find-hospitals"
          title="Match hospitals to your case"
          lede="Ranked fit against verified centre profiles, cancer type, city, treatments, languages, and international-patient services. Scores reflect directory fit, not clinical outcomes or live bed counts."
        />

        <form
          onSubmit={runMatch}
          className="rounded-[22px] border border-hair bg-paper p-4 sm:p-5 space-y-4 mb-8"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block text-left">
              <span className="text-xs uppercase tracking-wide text-charcoal">Cancer type</span>
              <select
                className="mt-1.5 w-full h-12 rounded-2xl border border-hair bg-mist px-3 text-forest-ink"
                value={cancerType}
                onChange={(e) => setCancerType(e.target.value)}
                required
              >
                <option value="">Select…</option>
                {cancerOptions.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </label>
            <label className="block text-left">
              <span className="text-xs uppercase tracking-wide text-charcoal">Preferred city (optional)</span>
              <input
                className="mt-1.5 w-full h-12 rounded-2xl border border-hair bg-mist px-3 text-forest-ink"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g. Houston, New York, Mumbai"
              />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm text-forest-ink min-h-[44px]">
            <input
              type="checkbox"
              checked={international}
              onChange={(e) => setInternational(e.target.checked)}
              className="accent-[#0f3e17] size-4"
            />
            International patient / traveling for care
          </label>

          <button
            type="button"
            className="text-sm text-forest-ink underline-offset-2 hover:underline"
            onClick={() => setShowAdvanced((v) => !v)}
          >
            {showAdvanced ? 'Hide preferences' : 'Add treatment, language, or insurance preferences'}
          </button>

          {showAdvanced ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
              <label className="block text-left">
                <span className="text-xs uppercase tracking-wide text-charcoal">Treatment preference</span>
                <select
                  className="mt-1.5 w-full h-12 rounded-2xl border border-hair bg-mist px-3 text-forest-ink"
                  value={treatmentType}
                  onChange={(e) => setTreatmentType(e.target.value)}
                >
                  {TREATMENT_OPTIONS.map((opt) => (
                    <option key={opt || 'any'} value={opt}>{opt || 'Any / not sure'}</option>
                  ))}
                </select>
              </label>
              <label className="block text-left">
                <span className="text-xs uppercase tracking-wide text-charcoal">Preferred language</span>
                <select
                  className="mt-1.5 w-full h-12 rounded-2xl border border-hair bg-mist px-3 text-forest-ink"
                  value={preferredLanguage}
                  onChange={(e) => setPreferredLanguage(e.target.value)}
                >
                  {LANGUAGE_OPTIONS.map((opt) => (
                    <option key={opt || 'any'} value={opt}>{opt || 'Any'}</option>
                  ))}
                </select>
              </label>
              <label className="block text-left">
                <span className="text-xs uppercase tracking-wide text-charcoal">Insurance / scheme hint</span>
                <input
                  className="mt-1.5 w-full h-12 rounded-2xl border border-hair bg-mist px-3 text-forest-ink"
                  value={insurance}
                  onChange={(e) => setInsurance(e.target.value)}
                  placeholder="e.g. Aetna, UHC, Medicare"
                />
              </label>
              <label className="block text-left">
                <span className="text-xs uppercase tracking-wide text-charcoal">Member ID (eligibility)</span>
                <input
                  className="mt-1.5 w-full h-12 rounded-2xl border border-hair bg-mist px-3 text-forest-ink"
                  value={memberId}
                  onChange={(e) => setMemberId(e.target.value)}
                  placeholder="Insurance member ID"
                  autoComplete="off"
                />
              </label>
              <label className="block text-left">
                <span className="text-xs uppercase tracking-wide text-charcoal">Date of birth</span>
                <input
                  type="date"
                  className="mt-1.5 w-full h-12 rounded-2xl border border-hair bg-mist px-3 text-forest-ink"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                />
              </label>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-3">
            <Button
              type="submit"
              disabled={loading}
              className="h-12 px-6 rounded-full bg-forest-ink text-cream-paper hover:bg-forest-shadow inline-flex items-center"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Matching…
                </>
              ) : (
                <>
                  Match my case
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-12 px-5 rounded-full border-hair text-forest-ink"
              onClick={() => navigate('/find-hospitals')}
            >
              Browse directory
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-12 px-5 rounded-full border-hair text-forest-ink"
              onClick={() => navigate('/journey-builder')}
            >
              Build a care package
            </Button>
          </div>
        </form>

        {error ? (
          <div className="rounded-2xl border border-hair bg-[#f6e8e6] text-[#8a1f1f] px-4 py-3 mb-6 text-sm" role="alert">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="space-y-4 mb-6" aria-busy="true" aria-label="Matching centres">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-[22px] border border-hair bg-paper p-5 animate-pulse">
                <div className="h-3 w-16 bg-mist rounded mb-3" />
                <div className="h-6 w-2/3 bg-mist rounded mb-2" />
                <div className="h-4 w-1/3 bg-mist rounded" />
              </div>
            ))}
          </div>
        ) : null}

        <div ref={resultsRef} className="scroll-mt-6">
          {!hasRun && !loading ? (
            <div className="rounded-[22px] border border-dashed border-hair bg-mist/40 px-5 py-8 text-center">
              <p className="text-base font-medium text-forest-ink">Ready when you are</p>
              <p className="text-sm text-charcoal mt-2 max-w-md mx-auto leading-relaxed">
                Choose a cancer type and match. You will get a ranked shortlist with fit reasons, listed specialists, and next steps into care packages or second opinion.
              </p>
            </div>
          ) : null}

          {result ? (
            <div className="space-y-6">
              {recommendation ? (
                <section className="rounded-[22px] border border-hair bg-mist/70 p-5 sm:p-6">
                  <h2 className="text-lg font-semibold text-forest-ink mb-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    How these centres fit
                  </h2>
                  <p className="text-sm text-forest-ink leading-relaxed whitespace-pre-wrap">{recommendation}</p>
                  {result.match_score != null ? (
                    <p className="text-xs text-charcoal mt-3">
                      Overall directory fit {result.match_score}
                      {result.matching_version ? ` · ${result.matching_version}` : ''}
                      {', '}
                      {result.disclaimer || 'Directory fit for planning your next conversation.'}
                    </p>
                  ) : null}
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Link
                      to={`/journey-builder?${packageQuery}`}
                      className="inline-flex items-center justify-center min-h-10 px-4 rounded-full bg-forest-ink text-cream-paper text-sm"
                    >
                      Build Value / Balanced / Comfort packages
                    </Link>
                    <Link
                      to="/second-opinion"
                      className="inline-flex items-center justify-center min-h-10 px-4 rounded-full border border-hair text-forest-ink text-sm"
                    >
                      Second opinion brief
                    </Link>
                  </div>
                </section>
              ) : null}

              {shortlist.length ? (
                <section>
                  <h2 className="text-lg font-semibold text-forest-ink mb-3">Top shortlist</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {shortlist.map((item) => (
                      <div key={item.hospitalId || item.rank} className="match-shortlist-card">
                        <p className="text-xs uppercase tracking-wide text-charcoal">#{item.rank}</p>
                        <p className="font-semibold text-forest-ink mt-1">{item.hospitalName}</p>
                        <p className="text-xs text-charcoal mt-1">{[item.city, item.country].filter(Boolean).join(', ')}</p>
                        {item.match_score != null ? (
                          <p className="text-sm text-forest-ink mt-2">Fit {item.match_score}</p>
                        ) : null}
                        {item.why?.[0] ? (
                          <p className="text-xs text-charcoal mt-2 leading-relaxed">{item.why[0]}</p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}

              <section>
                <h2 className="text-lg font-semibold text-forest-ink mb-4">
                  Ranked centres ({hospitals.length})
                </h2>
                {hospitals.length ? (
                  <div className="space-y-4">
                    {hospitals.map((hospital, index) => (
                      <article
                        key={hospital.id || hospital.name}
                        className="rounded-[22px] border border-hair bg-paper p-5"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-xs uppercase tracking-wide text-charcoal">#{index + 1}</p>
                              {hospital.match_score != null ? (
                                <span className="text-xs rounded-full border border-hair px-2 py-0.5 text-forest-ink">
                                  Fit {hospital.match_score}
                                </span>
                              ) : null}
                            </div>
                            <h3 className="text-xl font-semibold text-forest-ink mt-1">{hospital.name}</h3>
                            <p className="text-sm text-charcoal mt-1 flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5" />
                              {[hospital.city, hospital.state || hospital.country].filter(Boolean).join(', ')}
                            </p>
                            {hospital.designation ? (
                              <p className="text-sm text-forest-ink mt-2">{hospital.designation}</p>
                            ) : null}
                            {hospital.match_reasons?.length ? (
                              <ul className="mt-3 space-y-1">
                                {hospital.match_reasons.slice(0, 4).map((reason) => (
                                  <li key={reason} className="text-xs text-charcoal leading-relaxed">
                                    · {reason}
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                            {hospital.packageTotal || hospital.capacity || hospital.eligibility ? (
                              <div className="match-live-panel">
                                {hospital.eligibility?.coverageStatus ? (
                                  <p>
                                    Coverage: {hospital.eligibility.coverageStatus}
                                    {hospital.eligibility.inNetwork != null
                                      ? ` · ${hospital.eligibility.inNetwork ? 'in-network' : 'out-of-network'}`
                                      : ''}
                                  </p>
                                ) : null}
                                {hospital.packageTotal?.medicalPatientShareMax != null ? (
                                  <p>
                                    Live patient-share band: ${Number(hospital.packageTotal.medicalPatientShareMin || 0).toLocaleString()}
                                    {' - '}
                                    ${Number(hospital.packageTotal.medicalPatientShareMax).toLocaleString()}
                                  </p>
                                ) : null}
                                {hospital.capacity?.inventory?.estimatedNewPatientWaitDays != null ? (
                                  <p>
                                    New-patient wait ~{hospital.capacity.inventory.estimatedNewPatientWaitDays} days
                                    {hospital.capacity.inventory.openSlots != null
                                      ? ` · ${hospital.capacity.inventory.openSlots} open slots`
                                      : ''}
                                  </p>
                                ) : null}
                              </div>
                            ) : null}
                            {hospital.specialties?.length || hospital.cancer_types?.length || hospital.specializations?.length ? (
                              <p className="text-xs text-charcoal mt-2">
                                Focus areas: {(hospital.specialties || hospital.cancer_types || hospital.specializations || []).slice(0, 6).join(', ')}
                              </p>
                            ) : null}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Link
                              to={`/find-hospitals?hospital=${encodeURIComponent(hospital.id || hospital.name)}`}
                              className="inline-flex items-center justify-center min-h-10 px-4 rounded-full bg-forest-ink text-cream-paper text-sm"
                            >
                              Centre detail
                            </Link>
                            <Link
                              to="/journey-builder"
                              className="inline-flex items-center justify-center min-h-10 px-4 rounded-full border border-hair text-forest-ink text-sm"
                            >
                              Care package
                            </Link>
                            <Link
                              to="/teleconsultation"
                              className="inline-flex items-center justify-center min-h-10 px-4 rounded-full border border-hair text-forest-ink text-sm"
                            >
                              Video consult
                            </Link>
                            <Link
                              to="/second-opinion"
                              className="inline-flex items-center justify-center min-h-10 px-4 rounded-full border border-hair text-forest-ink text-sm"
                            >
                              Second opinion
                            </Link>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-charcoal rounded-2xl border border-hair bg-mist/50 px-4 py-3">
                    No verified centres matched those filters. Try another city, or browse the full directory.
                  </p>
                )}
              </section>

              {doctors.length ? (
                <section>
                  <h2 className="text-lg font-semibold text-forest-ink mb-4 flex items-center gap-2">
                    <Stethoscope className="w-4 h-4" />
                    Listed specialists ({doctors.length})
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {doctors.slice(0, 12).map((doc) => (
                      <div key={doc.id || doc.name} className="rounded-2xl border border-hair bg-paper p-4">
                        <p className="font-medium text-forest-ink">{doc.name}</p>
                        <p className="text-sm text-charcoal mt-1">
                          {[doc.specialty || doc.specialization, doc.hospital_name || doc.hospital].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                    ))}
                  </div>
                  <Link to="/find-oncologists" className="inline-flex text-sm text-forest-ink mt-4 underline-offset-2 hover:underline">
                    Browse all oncologists
                  </Link>
                </section>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
